// src/features/streams/components/schedule/ScheduleListItem.tsx

import { Group, Stack, Text, Badge, ActionIcon, Tooltip, ThemeIcon } from '@mantine/core';
import { IconPencil, IconTrash, IconHeading, IconFileDescription, IconTags } from '@tabler/icons-react';
import type { IMediaStreamScheduleAllData } from '@lib2/model/stream/MediaStreamScheduleTypes';
import { StreamScheduleUtils } from '../../StreamScheduleUtils';

interface ScheduleListItemProps {
  scheduleAll: IMediaStreamScheduleAllData;
  zoneId: string;
  nowSecs: number;
  /** Omit for expired rows (delete-only) */
  onEdit?: (s: IMediaStreamScheduleAllData) => void;
  onDelete: (s: IMediaStreamScheduleAllData) => void;
  dimmed?: boolean;
}

export function ScheduleListItem({
  scheduleAll,
  zoneId,
  nowSecs,
  onEdit,
  onDelete,
  dimmed = false,
}: ScheduleListItemProps) {
  const s = scheduleAll.schedule;
  const isRecurring = s.interval != null && s.intervalType != null;
  const frequencyLabel = StreamScheduleUtils.getFrequencyLabel(s.interval, s.intervalType);
  const windowLabel = StreamScheduleUtils.formatScheduleWindow(s, zoneId);
  const next = isRecurring ? StreamScheduleUtils.getNextOccurrence(s, zoneId, nowSecs) : null;

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      p="sm"
      opacity={dimmed ? 0.6 : 1}
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-md)',
      }}
    >
      <Stack gap={4}>
        <Group gap="xs">
          <Badge variant="light" color={isRecurring ? 'blue' : 'gray'} size="sm">
            {frequencyLabel}
          </Badge>
          {s.mediaTitle != null && (
            <Tooltip label={`Custom title: ${s.mediaTitle}`}>
              <ThemeIcon variant="light" color="gray" size="sm">
                <IconHeading size={12} />
              </ThemeIcon>
            </Tooltip>
          )}
          {s.mediaOverrideDescShort && (
            <Tooltip label="Custom description">
              <ThemeIcon variant="light" color="gray" size="sm">
                <IconFileDescription size={12} />
              </ThemeIcon>
            </Tooltip>
          )}
          {s.mediaOverrideTags && (
            <Tooltip label={`Custom tags: ${scheduleAll.tags.length > 0 ? scheduleAll.tags.join(', ') : 'none'}`}>
              <ThemeIcon variant="light" color="gray" size="sm">
                <IconTags size={12} />
              </ThemeIcon>
            </Tooltip>
          )}
        </Group>
        <Text size="sm" fw={500}>{windowLabel}</Text>
        {next && (
          <Text size="xs" c="dimmed">
            Next: {StreamScheduleUtils.formatOccurrence(next.startUtc, zoneId)}
          </Text>
        )}
        {dimmed && (
          <Text size="xs" c="dimmed">Expired</Text>
        )}
      </Stack>

      <Group gap="xs" wrap="nowrap">
        {onEdit && (
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => onEdit(scheduleAll)}
            aria-label="Edit schedule"
          >
            <IconPencil size={16} />
          </ActionIcon>
        )}
        <ActionIcon
          variant="subtle"
          color="red"
          onClick={() => onDelete(scheduleAll)}
          aria-label="Delete schedule"
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    </Group>
  );
}
